/**
 * Widget Library · v2.7.16
 * ─────────────────────────
 * Registry-driven set of admin dashboard widgets. Each widget is a small
 * self-contained card that:
 *   · Pulls its data from a shared `/admin/widget-feed` payload (polled
 *     every 30 s by the parent) OR from existing hooks/props
 *   · Renders inside `<DraggableStatsGrid>` and so respects drag / resize
 *   · Has a `defaultSize` hint the registry surfaces to new-add flows
 *
 * Add a new widget = add one entry to WIDGET_REGISTRY at the bottom of
 * this file. The Widget Selector + AdminDashboardPage consume the registry
 * directly so the new card is immediately addable from the UI.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import {
  Map as MapIcon, MessageCircle, DollarSign, Wrench, Users, Zap,
  Shield, UserCircle2, TrendingUp, Car, Clock,
  CheckCircle2, AlertCircle, Wifi, Banknote, CreditCard,
  Download,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import useCountUp from "@/hooks/useCountUp";

// ============================================================================
// Shared shell — gives every widget a consistent look + click-to-navigate.
// ============================================================================
function WidgetShell({ title, Icon, accent = "amber", value, sub, footer, onOpen, testId, children }) {
  // v2.7.24 · Hoisted to top of component so the hook is called every
  // render (React hooks rules). When `value` is undefined, the hook
  // gracefully passes through.
  const displayValue = useCountUp(value);
  const ringByAccent = {
    amber: "border-amber-400/30 hover:border-amber-400/60 hover:shadow-[0_0_24px_rgba(251,191,36,0.18)]",
    emerald: "border-emerald-400/30 hover:border-emerald-400/60 hover:shadow-[0_0_24px_rgba(52,211,153,0.18)]",
    sky:     "border-sky-400/30 hover:border-sky-400/60 hover:shadow-[0_0_24px_rgba(56,189,248,0.18)]",
    violet:  "border-violet-400/30 hover:border-violet-400/60 hover:shadow-[0_0_24px_rgba(167,139,250,0.18)]",
    red:     "border-red-400/30 hover:border-red-400/60 hover:shadow-[0_0_24px_rgba(248,113,113,0.18)]",
    fuchsia: "border-fuchsia-400/30 hover:border-fuchsia-400/60 hover:shadow-[0_0_24px_rgba(232,121,249,0.18)]",
  };
  const accentText = {
    amber: "text-amber-300", emerald: "text-emerald-300", sky: "text-sky-300",
    violet: "text-violet-300", red: "text-red-300", fuchsia: "text-fuchsia-300",
  };
  // v2.7.23 · 1px accent-tinted underline beneath every widget title.
  // Cohesion: the same horizontal-line system that runs through the top
  // nav now extends down into each widget header. Each card's accent
  // (sky / amber / emerald / etc.) automatically tints its own line.
  const accentBorder = {
    amber:   "border-amber-400/40",
    emerald: "border-emerald-400/40",
    sky:     "border-sky-400/40",
    violet:  "border-violet-400/40",
    red:     "border-red-400/40",
    fuchsia: "border-fuchsia-400/40",
  };
  return (
    <Card
      className={`relative h-full w-full bg-gradient-to-br from-zinc-900 via-zinc-950 to-black ${ringByAccent[accent]} p-3 transition-all duration-200 flex flex-col ${onOpen ? "cursor-pointer" : ""}`}
      onClick={onOpen}
      data-testid={testId}
    >
      <div className={`flex items-center justify-between pb-1.5 mb-1.5 border-b ${accentBorder[accent] || "border-zinc-700/40"}`}>
        <div className="flex items-center gap-1.5 min-w-0">
          {Icon ? <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${accentText[accent]}`}/> : null}
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold truncate">{title}</div>
        </div>
      </div>
      {value !== undefined && (
        <div className={`text-2xl font-extrabold tabular-nums ${accentText[accent]} leading-tight`} data-testid={`${testId}-value`}>{displayValue}</div>
      )}
      {sub !== undefined && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
      {children}
      {footer && <div className="text-[9px] text-zinc-600 mt-1.5 pt-1.5 border-t border-zinc-800/60">{footer}</div>}
    </Card>
  );
}

// ============================================================================
// 1. Live Map — compact map snapshot widget
// ============================================================================
export function LiveMapWidget({ feed }) {
  const nav = useNavigate();
  const drivers = feed?.drivers_breakdown || {};
  const online = drivers.online ?? 0;
  return (
    <WidgetShell
      title="Live Map"
      Icon={MapIcon}
      accent="sky"
      value={online}
      sub="drivers visible on map"
      footer="Click → Open Live Map"
      onOpen={() => nav("/admin/dashboard#tab=live")}
      testId="widget-live-map"
    >
      <div className="grid grid-cols-3 gap-1 mt-1.5 text-[9px]">
        <div className="text-center"><div className="text-zinc-100 font-bold">{drivers.on_trip ?? 0}</div><div className="text-zinc-500">on trip</div></div>
        <div className="text-center"><div className="text-zinc-100 font-bold">{drivers.waiting ?? 0}</div><div className="text-zinc-500">waiting</div></div>
        <div className="text-center"><div className="text-zinc-100 font-bold">{drivers.completing ?? 0}</div><div className="text-zinc-500">completing</div></div>
      </div>
    </WidgetShell>
  );
}

// ============================================================================
// 2. Chat Count — unread chats + sessions today
// ============================================================================
export function ChatCountWidget({ feed }) {
  const nav = useNavigate();
  const chat = feed?.chat || {};
  return (
    <WidgetShell
      title="Chat Count"
      Icon={MessageCircle}
      accent="violet"
      value={chat.unread ?? 0}
      sub="unread support threads"
      footer={`${chat.sessions_today ?? 0} active sessions · open chat hub`}
      onOpen={() => nav("/admin/dashboard#tab=chat")}
      testId="widget-chat-count"
    />
  );
}

// ============================================================================
// 3. Earnings — revenue velocity snapshot
// ============================================================================
export function EarningsWidget({ stats }) {
  const nav = useNavigate();
  const today = Number(stats?.revenue_today || 0);
  const total = Number(stats?.revenue_total || 0);
  const fmt = (n) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Velocity hint: today / 24h as $/hr (rough since we don't track hourly)
  const hrly = (today / 24) || 0;
  return (
    <WidgetShell
      title="Earnings"
      Icon={DollarSign}
      accent="amber"
      value={fmt(today)}
      sub="today · America/Chicago"
      footer={`≈ ${fmt(hrly)}/hr velocity · lifetime ${fmt(total)}`}
      onOpen={() => nav("/admin/dashboard#tab=earnings")}
      testId="widget-earnings"
    >
      <div className="flex items-center gap-1 mt-1 text-[9px] text-emerald-300">
        <TrendingUp className="h-3 w-3"/>
        <span>live · auto-refresh 30 s</span>
      </div>
    </WidgetShell>
  );
}

// ============================================================================
// 4. Fleet — vehicle state tracker
// ============================================================================
export function FleetWidget({ feed }) {
  const nav = useNavigate();
  const f = feed?.fleet || {};
  return (
    <WidgetShell
      title="Fleet"
      Icon={Wrench}
      accent="emerald"
      value={f.active ?? 0}
      sub={`active of ${f.total ?? 0} total vehicles`}
      footer="Click → Fleet management"
      onOpen={() => nav("/admin/dashboard#tab=fleet")}
      testId="widget-fleet"
    >
      <div className="grid grid-cols-2 gap-1 mt-1.5 text-[9px]">
        <div className="flex items-center gap-1">
          <Wrench className="h-2.5 w-2.5 text-amber-300"/>
          <span className="text-zinc-100 font-bold">{f.maintenance ?? 0}</span>
          <span className="text-zinc-500">maint.</span>
        </div>
        <div className="flex items-center gap-1">
          <AlertCircle className="h-2.5 w-2.5 text-red-300"/>
          <span className="text-zinc-100 font-bold">{f.out_of_service ?? 0}</span>
          <span className="text-zinc-500">OOS</span>
        </div>
      </div>
    </WidgetShell>
  );
}

// ============================================================================
// 5. Active Drivers — breakdown on-trip / waiting / completing
// ============================================================================
export function ActiveDriversWidget({ feed }) {
  const nav = useNavigate();
  const d = feed?.drivers_breakdown || {};
  const total = (d.on_trip || 0) + (d.waiting || 0) + (d.completing || 0);
  return (
    <WidgetShell
      title="Active Drivers"
      Icon={Car}
      accent="sky"
      value={total}
      sub="drivers currently working"
      footer="Click → Driver management"
      onOpen={() => nav("/admin/dashboard#tab=driver-mgmt")}
      testId="widget-active-drivers"
    >
      <div className="grid grid-cols-3 gap-1 mt-1.5 text-[9px]">
        <div className="text-center"><div className="text-sky-300 font-extrabold">{d.on_trip ?? 0}</div><div className="text-zinc-500">on trip</div></div>
        <div className="text-center"><div className="text-emerald-300 font-extrabold">{d.waiting ?? 0}</div><div className="text-zinc-500">waiting</div></div>
        <div className="text-center"><div className="text-amber-300 font-extrabold">{d.completing ?? 0}</div><div className="text-zinc-500">completing</div></div>
      </div>
    </WidgetShell>
  );
}

// ============================================================================
// 6. Active Dispatch — pending offers + active trip count
// ============================================================================
export function ActiveDispatchWidget({ feed }) {
  const nav = useNavigate();
  const d = feed?.dispatch || {};
  return (
    <WidgetShell
      title="Active Dispatch"
      Icon={Zap}
      accent="amber"
      value={d.pending_offers ?? 0}
      sub="pending offers · auto-assignments"
      footer={`${d.active_trips ?? 0} trips in motion · open coordinator`}
      onOpen={() => nav("/admin/dashboard#tab=driver-mgmt&sub=coordination")}
      testId="widget-active-dispatch"
    >
      <div className="flex items-center gap-1 mt-1 text-[9px] text-amber-300">
        <Clock className="h-3 w-3"/>
        <span>live · ride_offers stream</span>
      </div>
    </WidgetShell>
  );
}

// ============================================================================
// 7. Active Admins — staff currently monitoring
// ============================================================================
export function ActiveAdminsWidget({ feed }) {
  const nav = useNavigate();
  const a = feed?.admins || {};
  const list = (a.list || []).slice(0, 4);
  return (
    <WidgetShell
      title="Active Admins"
      Icon={Shield}
      accent="emerald"
      value={a.online ?? 0}
      sub="staff watching dispatch now"
      footer="Click → User management"
      onOpen={() => nav("/admin/dashboard#tab=user-mgmt&sub=users-admins")}
      testId="widget-active-admins"
    >
      {list.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {list.map((u) => (
            <span key={u} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-400/15 text-emerald-300 font-mono">{u}</span>
          ))}
        </div>
      )}
    </WidgetShell>
  );
}

// ============================================================================
// 8. Active Customers — riders exploring or pending match
// ============================================================================
export function ActiveCustomersWidget({ feed }) {
  const nav = useNavigate();
  const c = feed?.customers || {};
  return (
    <WidgetShell
      title="Active Customers"
      Icon={UserCircle2}
      accent="fuchsia"
      value={c.active_total ?? 0}
      sub="riders on the platform"
      footer={`${c.online_now ?? 0} currently exploring · open user mgmt`}
      onOpen={() => nav("/admin/dashboard#tab=user-mgmt&sub=users-riders")}
      testId="widget-active-customers"
    >
      <div className="flex items-center gap-1 mt-1 text-[9px] text-fuchsia-300">
        <Wifi className="h-3 w-3"/>
        <span>session telemetry</span>
      </div>
    </WidgetShell>
  );
}

// ============================================================================
// 9. Cash Today — total cash collected today (relocated from OverviewTab)
// ============================================================================
export function CashTodayWidget({ feed }) {
  const nav = useNavigate();
  const cash = feed?.cash || {};
  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <WidgetShell
      title="Cash Today"
      Icon={Banknote}
      accent="emerald"
      value={fmt(cash.today_total)}
      sub="reported cash · today (UTC)"
      footer={`${cash.today_count ?? 0} cash transaction${cash.today_count === 1 ? "" : "s"} · open earnings`}
      onOpen={() => nav("/admin/dashboard#tab=earnings")}
      testId="widget-cash-today"
    />
  );
}

// ============================================================================
// 10. Recent Card $ — last 24h card revenue (relocated from OverviewTab)
// ============================================================================
export function RecentCardWidget({ feed }) {
  const nav = useNavigate();
  const card = feed?.card_payments || {};
  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <WidgetShell
      title="Recent Card $"
      Icon={CreditCard}
      accent="amber"
      value={fmt(card.total_24h)}
      sub="card revenue · last 24h"
      footer={`${card.count_24h ?? 0} card payment${card.count_24h === 1 ? "" : "s"} · open accounting`}
      onOpen={() => nav("/admin/dashboard#tab=admin&sub=payments")}
      testId="widget-recent-card"
    />
  );
}

// ============================================================================
// Hook — single source of truth for widget feed (30 s polling)
// ============================================================================
export function useWidgetFeed(enabled = true) {
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await adminApi.get("/admin/widget-feed");
        if (!cancelled) setFeed(data);
      } catch { /* silent — keep last good payload */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);
  return feed;
}

// ============================================================================
// Widget Registry — single source of truth for the Widget Selector
// ============================================================================
// Each entry:
//   id        · stable string, used as React key + persistence key
//   label     · short title shown in Customize popover + selector
//   group     · "stats" (the 6 originals) | "extras" (the 8 new ones)
//   defaultSize · "sm" | "md" | "lg" | "xl"
//   defaultOn · whether the widget is in the default user layout
//   Icon      · lucide icon for the selector card
//   description · 1-liner shown in selector
//   needsFeed · true if the widget consumes `/admin/widget-feed`
//   render(ctx) · returns the React element. ctx = { stats, feed }
// ----------------------------------------------------------------------------

// ============================================================================
// 9b/c. Weather & Emergency tickers — v2.7.30
//       (Hoisted ABOVE the registry so the inline `render` arrows resolve.)
// ============================================================================
function ActiveWarningsTickerWidget() {
  const nav = useNavigate();
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    const load = () => {
      const tok = localStorage.getItem("rtg_admin_token");
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/weather/alerts/active`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      }).then((r) => r.ok ? r.json() : []).then(setAlerts).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    const onWx = () => load();
    window.addEventListener("rtg:weather_alert", onWx);
    return () => { clearInterval(id); window.removeEventListener("rtg:weather_alert", onWx); };
  }, []);
  const warnings = alerts.filter((a) => /Warning/i.test(a.event));
  const watches = alerts.filter((a) => /Watch/i.test(a.event));
  const top = warnings[0] || watches[0];
  const isWarning = !!warnings[0];
  const fmt = (iso) => {
    if (!iso) return "—";
    const mins = Math.round((new Date(iso) - new Date()) / 60000);
    if (mins < 0) return "expired";
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };
  return (
    <Card
      className={`relative h-full w-full bg-gradient-to-br from-zinc-900 via-zinc-950 to-black p-3 flex flex-col transition-all duration-200 cursor-pointer ${warnings.length ? "ring-1 ring-red-400/60" : watches.length ? "ring-1 ring-amber-400/60" : "ring-1 ring-zinc-700/40"}`}
      onClick={() => nav("/admin/weather#sub=warnings")}
      data-testid="widget-active-warnings"
    >
      <div className={`flex items-center justify-between pb-1.5 mb-1.5 border-b ${warnings.length ? "border-red-400/40" : watches.length ? "border-amber-400/40" : "border-zinc-700/40"}`}>
        <div className="flex items-center gap-1.5 min-w-0">
          <AlertCircle className={`h-3.5 w-3.5 flex-shrink-0 ${warnings.length ? "text-red-300" : watches.length ? "text-amber-300" : "text-zinc-400"}`}/>
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold truncate">Active Warnings</div>
        </div>
      </div>
      <div className="flex items-baseline gap-3">
        <span className="text-2xl font-extrabold tabular-nums text-red-300 leading-tight">{warnings.length}</span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">warnings</span>
        <span className="text-2xl font-extrabold tabular-nums text-amber-300 leading-tight ml-2">{watches.length}</span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">watches</span>
      </div>
      {top ? (
        <div className="text-[10px] text-zinc-400 mt-1 line-clamp-2">
          <span className={isWarning ? "text-red-300 font-bold" : "text-amber-300 font-bold"}>{top.event}</span>
          <span className="mx-1">·</span>
          <span className="tabular-nums">{fmt(top.expires || top.ends)}</span>
        </div>
      ) : (
        <div className="text-[10px] text-zinc-500 mt-1">No active NWS hazards · open Weather Commander</div>
      )}
    </Card>
  );
}

function StateAlertsWidget() {
  const nav = useNavigate();
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    const load = () => {
      const tok = localStorage.getItem("rtg_admin_token");
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/weather/alerts/state`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      }).then((r) => r.ok ? r.json() : []).then(setAlerts).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    const onWx = () => load();
    window.addEventListener("rtg:weather_alert", onWx);
    return () => { clearInterval(id); window.removeEventListener("rtg:weather_alert", onWx); };
  }, []);
  const top = alerts[0];
  const cat = top?.state_category;
  const tint = {
    AMBER: "text-amber-300",
    SILVER: "text-slate-300",
    BLUE: "text-blue-300",
    KASEY: "text-fuchsia-300",
  }[cat] || "text-fuchsia-300";
  return (
    <Card
      className={`relative h-full w-full bg-gradient-to-br from-zinc-900 via-zinc-950 to-black p-3 flex flex-col transition-all duration-200 cursor-pointer ${alerts.length ? "ring-1 ring-fuchsia-400/60" : "ring-1 ring-zinc-700/40"}`}
      onClick={() => nav("/admin/state-emergencies")}
      data-testid="widget-state-alerts"
    >
      <div className={`flex items-center justify-between pb-1.5 mb-1.5 border-b ${alerts.length ? "border-fuchsia-400/40" : "border-zinc-700/40"}`}>
        <div className="flex items-center gap-1.5 min-w-0">
          <Shield className={`h-3.5 w-3.5 flex-shrink-0 ${alerts.length ? "text-fuchsia-300" : "text-zinc-400"}`}/>
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold truncate">State Alerts</div>
        </div>
      </div>
      <div className="text-2xl font-extrabold tabular-nums text-fuchsia-300 leading-tight">{alerts.length}</div>
      {top ? (
        <div className="text-[10px] mt-1 line-clamp-2">
          <span className={`${tint} font-bold uppercase`}>{cat || "Alert"}</span>
          <span className="text-zinc-400 mx-1">·</span>
          <span className="text-zinc-300">{top.event}</span>
        </div>
      ) : (
        <div className="text-[10px] text-zinc-500 mt-1">No active state alerts · open feed</div>
      )}
    </Card>
  );
}

// ============================================================================
// v2.7.65+E122 · Manual Downloads Widget — L5 dashboard card that shows the
// last 5 wiki-PDF pulls (who, when, size). Powered by the E121 audit trail.
// ============================================================================
export function ManualDownloadsWidget() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await adminApi.get("/admin/wiki/manual/downloads", { params: { limit: 5 } });
        if (!cancelled) { setRows(r.data?.rows || []); setError(false); }
      } catch (e) {
        if (!cancelled) {
          setError(true);
          // 403 = user is < L5 → we hide the widget entirely.
          if (e?.response?.status === 403) setRows("hidden");
        }
      }
    };
    load();
    const t = setInterval(load, 60_000);   // refresh every minute
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (rows === "hidden") return null;

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return ""; }
  };
  const shortEmail = (e) => {
    if (!e) return "system";
    const [u] = e.split("@");
    return u || e;
  };

  return (
    <WidgetShell
      title="Recent Manual Downloads"
      Icon={Download}
      accent="fuchsia"
      onOpen={() => nav("/admin/wiki")}
      testId="widget-manual-downloads"
      footer={rows && Array.isArray(rows) && rows.length === 0 ? "No downloads yet this week" : "Click → Knowledge Center"}
    >
      {rows === null && !error && (
        <div className="text-[10px] text-zinc-500 py-2">Loading…</div>
      )}
      {error && rows !== "hidden" && rows === null && (
        <div className="text-[10px] text-red-400 py-2">Failed to load audit trail</div>
      )}
      {Array.isArray(rows) && rows.length > 0 && (
        <ul className="mt-1 space-y-1" data-testid="widget-manual-downloads-list">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="flex items-center gap-1.5 min-w-0">
                <span className={`inline-block px-1 rounded text-[9px] uppercase tracking-widest font-bold ${
                  r.kind === "master"
                    ? "bg-rose-500/20 text-rose-300"
                    : "bg-amber-500/20 text-amber-300"
                }`}>
                  {r.kind}
                </span>
                <span className="truncate text-zinc-200 font-medium" title={r.user_email || r.user_username || ""}>
                  {shortEmail(r.user_email || r.user_username)}
                </span>
              </span>
              <span className="text-[10px] text-zinc-500 flex-shrink-0" title={r.downloaded_at}>
                {fmtTime(r.downloaded_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </WidgetShell>
  );
}


export const WIDGET_REGISTRY = [
  // Originals (stats) — these consume the existing `stats` prop directly.
  // They are rendered by AdminDashboardPage's own stat-box code, so the
  // registry only carries metadata here (not a render fn).
  { id: "drivers",        label: "Drivers",         group: "stats", defaultSize: "md", defaultOn: true, Icon: Users,      description: "Total roster" },
  { id: "active",         label: "Active Drivers",  group: "stats", defaultSize: "md", defaultOn: true, Icon: CheckCircle2, description: "On platform · verified" },
  { id: "online",         label: "Online Drivers",  group: "stats", defaultSize: "md", defaultOn: true, Icon: Wifi,       description: "Live drivers" },
  { id: "trips",          label: "Trips",           group: "stats", defaultSize: "md", defaultOn: true, Icon: TrendingUp, description: "Lifetime trips" },
  { id: "revenue-today",  label: "Revenue Today",   group: "stats", defaultSize: "md", defaultOn: true, Icon: DollarSign, description: "Today's gross" },
  { id: "revenue-total",  label: "Revenue Total",   group: "stats", defaultSize: "md", defaultOn: true, Icon: DollarSign, description: "Lifetime gross" },
  // Extras — render fn provided.
  { id: "live-map",        label: "Live Map",          group: "extras", defaultSize: "lg", defaultOn: false, needsFeed: true,  Icon: MapIcon,       description: "Compact map snapshot",   render: (ctx) => <LiveMapWidget feed={ctx.feed}/> },
  { id: "chat-count",      label: "Chat Count",        group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true,  Icon: MessageCircle, description: "Unread support threads",  render: (ctx) => <ChatCountWidget feed={ctx.feed}/> },
  { id: "earnings",        label: "Earnings",          group: "extras", defaultSize: "lg", defaultOn: false, needsFeed: false, Icon: DollarSign,    description: "Revenue velocity snapshot", render: (ctx) => <EarningsWidget stats={ctx.stats}/> },
  { id: "fleet",           label: "Fleet",             group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true,  Icon: Wrench,        description: "Vehicle state tracker",   render: (ctx) => <FleetWidget feed={ctx.feed}/> },
  { id: "active-drivers",  label: "Active Drivers (live)", group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true, Icon: Car,        description: "On-trip · waiting · completing", render: (ctx) => <ActiveDriversWidget feed={ctx.feed}/> },
  { id: "active-dispatch", label: "Active Dispatch",   group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true,  Icon: Zap,           description: "Pending offers + auto-assignments", render: (ctx) => <ActiveDispatchWidget feed={ctx.feed}/> },
  { id: "active-admins",   label: "Active Admins",     group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true,  Icon: Shield,        description: "Staff currently monitoring", render: (ctx) => <ActiveAdminsWidget feed={ctx.feed}/> },
  { id: "active-customers",label: "Active Customers",  group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true,  Icon: UserCircle2,   description: "Riders exploring / waiting", render: (ctx) => <ActiveCustomersWidget feed={ctx.feed}/> },
  // v2.7.18 · Relocated from OverviewTab so the lower workspace stays clean.
  { id: "cash-today",      label: "Cash Today",         group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true,  Icon: Banknote,      description: "Total cash collected today",        render: (ctx) => <CashTodayWidget feed={ctx.feed}/> },
  { id: "recent-card",     label: "Recent Card $",      group: "extras", defaultSize: "md", defaultOn: false, needsFeed: true,  Icon: CreditCard,    description: "Card revenue · last 24h",           render: (ctx) => <RecentCardWidget feed={ctx.feed}/> },
  // v2.7.30 · Weather & Emergency Event Production tickers
  { id: "weather-warnings", label: "Active Warnings",    group: "extras", defaultSize: "lg", defaultOn: false, needsFeed: false, Icon: AlertCircle,   description: "Live NWS Warnings (red) + Watches (yellow) with countdown",      render: () => <ActiveWarningsTickerWidget/> },
  { id: "state-alerts",     label: "State Alerts",       group: "extras", defaultSize: "md", defaultOn: false, needsFeed: false, Icon: Shield,        description: "AMBER / Silver / Blue / Kasey alert stream",                     render: () => <StateAlertsWidget/> },
  // v2.7.65+E122 · Manual Downloads audit card (self-hides for non-L5 callers).
  // Registry entry is retained for the Widget Selector; the dashboard also
  // renders this widget inline outside the (currently hidden) widget grid.
  { id: "manual-downloads", label: "Manual Downloads",   group: "extras", defaultSize: "md", defaultOn: false, needsFeed: false, Icon: Download,      description: "Who pulled the wiki PDFs this week (L5 only)",                   render: () => <ManualDownloadsWidget/> },
];

export function getRegistryEntry(id) {
  return WIDGET_REGISTRY.find((w) => w.id === id) || null;
}

export function defaultActiveSet() {
  return WIDGET_REGISTRY.filter((w) => w.defaultOn).map((w) => w.id);
}
